import { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import DataTableMUI from "../../components/common/DataTable";
import { DigitalMediaColumns } from "../../services/tableColomn";
import ViewDataPopup from "./viewDataPopup";
import { useNavigate } from "react-router-dom";
import {
  deleteDigitalMedia,
  getAllDigitalMedia,
} from "../../redux/slices/digitalMediaSlice";

const statusOptions = [
  { value: "all", label: "ALL", color: "#2563eb" },
  { value: "active", label: "ACTIVE", color: "#388e3c" },
  { value: "inactive", label: "INACTIVE", color: "#bfa100" },
  { value: "deleted", label: "DELETED", color: "#d32f2f" },
];

const DigitalMedia = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { digitalMedia, loading, statusCounts, pagination, error } =
    useAppSelector((state) => state.digitalMedia);

  const [viewPopupOpen, setViewPopupOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortKey, setSortKey] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    dispatch(
      getAllDigitalMedia({
        status: statusFilter,
        page,
        search,
        limit: itemsPerPage,
        sortKey,
        sortOrder,
      })
    );
  }, [dispatch, statusFilter, page, search, itemsPerPage, sortKey, sortOrder]);

  const refetch = () =>
    dispatch(
      getAllDigitalMedia({
        status: statusFilter,
        page,
        search,
        limit: itemsPerPage,
        sortKey,
        sortOrder,
      })
    );

  const handleStatusChange = (status) => {
    setStatusFilter(status);
    setPage(1);
  };

  const handlePageChange = (newPage) => setPage(newPage);

  const handleView = (row) => {
    setSelectedRow(row);
    setViewPopupOpen(true);
  };

  const handleEdit = (row) => {
    navigate("/digital-media-form", {
      state: { initialData: row },
    });
  };

  const handleDelete = async (row) => {
    if (
      window.confirm("Are you sure you want to delete this electric media?")
    ) {
      try {
        await dispatch(deleteDigitalMedia(row._id)).unwrap();
        refetch();
      } catch (error) {
        console.error("Delete failed:", error);
      }
    }
  };

  const handleSort = (col) => {
    if (!col.sortable) return;
    setSortKey(col.accessor);
    setSortOrder(
      sortKey === col.accessor && sortOrder === "asc" ? "desc" : "asc"
    );
    setPage(1);
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  // Add counts to statusOptions
  const statusOpts = statusOptions.map((opt) => ({
    ...opt,
    count: statusCounts?.[opt.value] || 0,
  }));

  // Add focus event listener to refetch data when returning to page
  useEffect(() => {
    const handleFocus = () => {
      // Refetch data when user returns to this page
      dispatch(
        getAllDigitalMedia({
          status: statusFilter,
          page,
          search,
          limit: itemsPerPage,
          sortKey,
          sortOrder,
        })
      );
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [dispatch, statusFilter, page, search, itemsPerPage, sortKey, sortOrder]);

  // Force refetch when component mounts or becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible, refetch data
        dispatch(
          getAllDigitalMedia({
            status: statusFilter,
            page,
            search,
            limit: itemsPerPage,
            sortKey,
            sortOrder,
          })
        );
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [dispatch, statusFilter, page, search, itemsPerPage, sortKey, sortOrder]);

  // Debug logging to check data
  useEffect(() => {
    console.log("Digital Media State:", {
      data: digitalMedia,
      loading,
      statusCounts,
      pagination,
      error: error || "No error",
    });
  }, [digitalMedia, loading, statusCounts, pagination, error]);

  // Show error message if API call fails
  useEffect(() => {
    if (error) {
      console.error("Digital Media API Error:", error);
      alert(`Error loading data: ${error}`);
    }
  }, [error]);

  return (
    <div className="data-table">
      <DataTableMUI
        title="Digital Media Gallery"
        columns={DigitalMediaColumns}
        data={digitalMedia || []}
        statusOptions={statusOpts}
        statusFilter={statusFilter}
        onStatusFilterChange={handleStatusChange}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        pagination
        itemsPerPage={itemsPerPage}
        currentPage={pagination?.page || 1}
        totalItems={pagination?.total || 0}
        setItemsPerPage={setItemsPerPage}
        onPageChange={handlePageChange}
        searchable
        searchTerm={searchInput}
        setSearchTerm={setSearchInput}
        onSearch={handleSearch}
        onAdd
        onAddClick={() => navigate("/digital-media-form")}
        loading={loading}
        sortKey={sortKey}
        sortOrder={sortOrder}
        onSort={handleSort}
        draggable={true}
      />
      {selectedRow && (
        <ViewDataPopup
          open={viewPopupOpen}
          onClose={() => {
            setViewPopupOpen(false);
            setSelectedRow(null);
          }}
          data={selectedRow}
        />
      )}
    </div>
  );
};

export default DigitalMedia;
